import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { FormControlSize } from '@televet/kibble-ui/build/shared/types';
import React, { useEffect, useCallback, useRef } from 'react';

interface ICurrencyInputProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  readOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  size?: FormControlSize;
  errorText?: string;
}

const CurrencyInput = ({
  label,
  value,
  onChange,
  onBlur,
  readOnly = false,
  isRequired,
  isInvalid,
  placeholder,
  inputRef,
  errorText,
  size = 'md',
}: ICurrencyInputProps): JSX.Element => {
  const defaultInputRef = useRef<HTMLInputElement | null>(null);
  const ref = inputRef || defaultInputRef;

  useEffect(() => {
    ref?.current?.focus();
  }, [ref]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      if (isNaN(Number(value))) return;
      const decimalPlaces = value.includes('.') && value.split('.').pop();
      if (decimalPlaces && decimalPlaces.length > 2) return;
      onChange(value.trim());
    },
    [onChange],
  );

  const handleInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      const formattedValue = Number(event.target.value).toFixed(2).toString();
      onChange(formattedValue);
      if (typeof onBlur === 'function') onBlur();
    },
    [onChange, onBlur],
  );

  return (
    <TextInput
      label={label}
      labelStyle={{ fontWeight: 'semibold' }}
      type="number"
      inputMode="decimal"
      placeholder={placeholder || '0.00'}
      ref={ref}
      value={value}
      onFocus={(e): void => e.target.select()}
      onBlur={handleInputBlur}
      onChange={handleInputChange}
      isReadOnly={readOnly}
      isRequired={isRequired}
      isInvalid={isInvalid}
      size={size}
      data-testid="currency-input"
      errorText={errorText}
    />
  );
};

export default CurrencyInput;
